import React, { useState, useEffect, useRef } from 'react';
import { initializeApp } from "firebase/app";
import {
  getFirestore, doc, getDoc, setDoc, updateDoc, onSnapshot,
  collection, addDoc, query, orderBy, limit, deleteDoc,
  getDocs, increment
} from "firebase/firestore";
import { getAuth, signInAnonymously, onAuthStateChanged, EmailAuthProvider, linkWithCredential, signInWithEmailAndPassword } from "firebase/auth";

import { CARD_LIBRARY, ASH_VALUES } from './Cards';
import { PACK_TYPES } from './Packs';
import { BottomBanner, watchRewardedAd } from './AdController';
import FusionCase from './FusionCase';
import './App.css';

// ==========================================
// DOMAIN PROTECTION
// ==========================================
const ALLOWED_DOMAINS = ['localhost', 'infpixccg.web.app'];
if (typeof window !== 'undefined' && !ALLOWED_DOMAINS.includes(window.location.hostname)) {
  console.warn('Unauthorized domain access detected');
  // Optionally redirect or show error
}

// ==========================================
// FIREBASE CONFIG
// ==========================================
const firebaseConfig = {
  apiKey: "AIzaSyAYk_xbV7ZDNAOZuKxc_OF3I14MO-ENsB4",
  authDomain: "ccg-game-cd803.firebaseapp.com",
  projectId: "ccg-game-cd803",
  storageBucket: "ccg-game-cd803.firebasestorage.app",
  messagingSenderId: "844503779699",
  appId: "1:844503779699:web:e6ee8ef8d5e76a83855d06",
  measurementId: "G-0LHW1MJ354"
};

const app  = initializeApp(firebaseConfig);
const db   = getFirestore(app);
const auth = getAuth(app);

// ==========================================
// CONSTANTS
// ==========================================
const RARITY_ORDER    = { Divine: 1, Mythic: 2, Legendary: 3, Epic: 4, Rare: 5, Common: 6 };
const RARITY_FALLBACK = ['Divine', 'Mythic', 'Legendary', 'Epic', 'Rare', 'Common'];
const ADMIN_UID       = "XEbmyZyxmnOpnyfuwWtuRyxglvG3";

// ── Foil system ───────────────────────────────────────────
// Stored in inventory as "CardName|foil" e.g. "Neon Dragon|gold"
// Foil is rolled AFTER rarity, independently of luck upgrades
// Cannot be obtained through fusion
const FOIL_TYPES = {
  silver:   { chance: 1,   multiplier: 2,  label: 'SILVER', color: '#c0c0c0' },
  gold:     { chance: 0.1, multiplier: 5,  label: 'GOLD',   color: '#ffd700' },
  rainbow:  { chance: 0.01, multiplier: 10, label: 'RAINBOW',color: '#ff88ff' },
};
// Roll foil out of 1000; no luck modifier
function rollFoil() {
  const roll = Math.random() * 1000;
  if (roll < 0.1)  return 'rainbow';
  if (roll < 1)    return 'gold';
  if (roll < 10)   return 'silver';
  return null;
}
// Parse inventory entry: "CardName|gold" → { name, foil }
function parseInvEntry(entry) {
  const parts = entry.split('|');
  return { name: parts[0], foil: parts[1] || null };
}
// Build inventory entry string
function buildInvEntry(name, foil) {
  return foil ? `${name}|${foil}` : name;
}
// Get power for an inventory entry, applying foil multiplier
function getEntryPower(entry) {
  const { name, foil } = parseInvEntry(entry);
  const card = CARD_LIBRARY.find(c => c.name === name);
  if (!card) return 0;
  const mult = foil ? (FOIL_TYPES[foil]?.multiplier || 1) : 1;
  return card.power * mult;
}

// ── Level system ──────────────────────────────────────────
function calcLevel(power) {
  const BASE = 1000;
  let level = 1, req = BASE;
  while (power >= req) { level++; req = Math.floor(BASE * Math.pow(1.1, level - 1)); }
  const currentReq = level <= 1 ? 0 : Math.floor(BASE * Math.pow(1.1, level - 2));
  const nextReq    = Math.floor(BASE * Math.pow(1.1, level - 1));
  const progress   = (power - currentReq) / Math.max(nextReq - currentReq, 1);
  return { level, currentReq, nextReq, progress: Math.min(Math.max(progress, 0), 1) };
}

// ── Cost Reduction Upgrade System ───────────────────────
const COST_REDUCTION_UPGRADES = [
  { tier: 1, cost: 10000, discount: 0.01, name: 'Cost Reduction I' },
  { tier: 2, cost: 100000, discount: 0.01, name: 'Cost Reduction II' },
  { tier: 3, cost: 1000000, discount: 0.01, name: 'Cost Reduction III' },
];

// Calculate discounted price
function getDiscountedPrice(basePrice, discountTier) {
  const totalDiscount = Math.min(discountTier * 0.01, 0.5); // Cap at 50% discount
  return Math.floor(basePrice * (1 - totalDiscount));
}

// ── Anti-cheat caps ───────────────────────────────────────
const FIELD_CAPS = {
  stars:    9_999_999,
  ash:      9_999_999,
  gems:     999_999,
  vault:    9_999_999,
  ashVault: 9_999_999,
  gemVault: 999_999,
  level:    9999,
  lPoints:  9999,
  costReductionTier: 3, // Max tier for cost reduction
};
// Valid card names set (base names without foil suffix)
const VALID_CARD_NAMES = new Set(CARD_LIBRARY.map(c => c.name));
const VALID_FOILS      = new Set(['silver', 'gold', 'rainbow']);

function sanitizePlayerData(data) {
  const out = { ...data };
  for (const [field, cap] of Object.entries(FIELD_CAPS)) {
    if (typeof out[field] === 'number') {
      if (out[field] > cap) out[field] = cap;
      if (out[field] < 0)   out[field] = 0;
    }
  }
  if (Array.isArray(out.inventory)) {
    out.inventory = out.inventory.filter(entry => {
      if (typeof entry !== 'string') return false;
      const { name, foil } = parseInvEntry(entry);
      if (!VALID_CARD_NAMES.has(name)) return false;
      if (foil && !VALID_FOILS.has(foil)) return false;
      return true;
    });
  }
  return out;
}

// ── Battle pass — 40 tiers, card rewards on specific tiers ──
// cardReward: name of a card from the 'battlepass' pool
const BP_TIERS = [
  { powerReq:    2000, stars:  800,  ash:  0,   gems: 0,  cardReward: null            },
  { powerReq:    5000, stars:  0,    ash:  40,  gems: 0,  cardReward: null            },
  { powerReq:    9000, stars:  1200, ash:  0,   gems: 0,  cardReward: null            },
  { powerReq:   14000, stars:  0,    ash:  80,  gems: 0,  cardReward: null            },
  { powerReq:   20000, stars:  1500, ash:  0,   gems: 0,  cardReward: null            },
  { powerReq:   28000, stars:  0,    ash:  120, gems: 0,  cardReward: null            },
  { powerReq:   38000, stars:  2000, ash:  0,   gems: 5,  cardReward: 'MONTHLY WARLORD' },
  { powerReq:   50000, stars:  0,    ash:  160, gems: 0,  cardReward: null            },
  { powerReq:   65000, stars:  2500, ash:  100, gems: 0,  cardReward: null            },
  { powerReq:   83000, stars:  0,    ash:  200, gems: 5,  cardReward: null            },
  { powerReq:  104000, stars:  3000, ash:  0,   gems: 0,  cardReward: null            },
  { powerReq:  128000, stars:  0,    ash:  260, gems: 0,  cardReward: null            },
  { powerReq:  155000, stars:  4000, ash:  0,   gems: 0,  cardReward: null            },
  { powerReq:  186000, stars:  0,    ash:  320, gems: 10, cardReward: 'BATTLE HERALD' },
  { powerReq:  221000, stars:  5000, ash:  0,   gems: 0,  cardReward: null            },
  { powerReq:  260000, stars:  0,    ash:  400, gems: 0,  cardReward: null            },
  { powerReq:  304000, stars:  6000, ash:  200, gems: 10, cardReward: null            },
  { powerReq:  353000, stars:  0,    ash:  480, gems: 0,  cardReward: null            },
  { powerReq:  408000, stars:  7000, ash:  0,   gems: 0,  cardReward: null            },
  { powerReq:  470000, stars:  0,    ash:  560, gems: 15, cardReward: null            },
  { powerReq:  540000, stars:  8000, ash:  0,   gems: 0,  cardReward: 'SEASON CHAMPION' },
  { powerReq:  619000, stars:  0,    ash:  640, gems: 0,  cardReward: null            },
  { powerReq:  708000, stars:  9000, ash:  0,   gems: 0,  cardReward: null            },
  { powerReq:  808000, stars:  0,    ash:  760, gems: 20, cardReward: null            },
  { powerReq:  920000, stars: 10000, ash:  400, gems: 0,  cardReward: null            },
  { powerReq: 1045000, stars:  0,    ash:  900, gems: 0,  cardReward: null            },
  { powerReq: 1184000, stars: 12000, ash:  0,   gems: 25, cardReward: null            },
  { powerReq: 1338000, stars:  0,    ash: 1040, gems: 0,  cardReward: null            },
  { powerReq: 1508000, stars: 14000, ash:  0,   gems: 0,  cardReward: null            },
  { powerReq: 1695000, stars:  0,    ash: 1200, gems: 30, cardReward: null            },
  { powerReq: 1900000, stars: 16000, ash:  0,   gems: 0,  cardReward: 'SEASON CHAMPION' },
  { powerReq: 2124000, stars:  0,    ash: 1400, gems: 0,  cardReward: null            },
  { powerReq: 2368000, stars: 18000, ash:  0,   gems: 35, cardReward: null            },
  { powerReq: 2633000, stars:  0,    ash: 1600, gems: 0,  cardReward: null            },
  { powerReq: 2920000, stars: 22000, ash:  0,   gems: 0,  cardReward: null            },
  { powerReq: 3230000, stars:  0,    ash: 1800, gems: 40, cardReward: null            },
  { powerReq: 3565000, stars: 26000, ash:  0,   gems: 0,  cardReward: null            },
  { powerReq: 3926000, stars:  0,    ash: 2000, gems: 50, cardReward: null            },
  { powerReq: 4315000, stars: 32000, ash: 1000, gems: 0,  cardReward: null            },
  { powerReq: 4733000, stars: 50000, ash: 4000, gems: 100,cardReward: 'SEASON CHAMPION' },
];

// ── 100-day daily rewards — streak lost on miss ───────────
// gems appear every 7th day and on day 100
const DAILY_REWARDS = [
  { stars:  500,  ash:  0,   gems: 0  }, // day 1
  { stars:  0,    ash:  30,  gems: 0  }, // day 2
  { stars:  800,  ash:  0,   gems: 0  }, // day 3
  { stars:  0,    ash:  60,  gems: 0  }, // day 4
  { stars:  1000, ash:  0,   gems: 0  }, // day 5
  { stars:  800,  ash:  80,  gems: 0  }, // day 6
  { stars:  2500, ash:  150, gems: 3  }, // day 7  ★GEM
  { stars:  600,  ash:  0,   gems: 0  }, // day 8
  { stars:  0,    ash:  70,  gems: 0  }, // day 9
  { stars:  1000, ash:  0,   gems: 0  }, // day 10
  { stars:  0,    ash:  100, gems: 0  }, // day 11
  { stars:  1200, ash:  0,   gems: 0  }, // day 12
  { stars:  1000, ash:  150, gems: 0  }, // day 13
  { stars:  3500, ash:  200, gems: 5  }, // day 14 ★GEM
  { stars:  700,  ash:  0,   gems: 0  }, // day 15
  { stars:  0,    ash:  90,  gems: 0  }, // day 16
  { stars:  1200, ash:  0,   gems: 0  }, // day 17
  { stars:  0,    ash:  130, gems: 0  }, // day 18
  { stars:  1500, ash:  0,   gems: 0  }, // day 19
  { stars:  1200, ash:  200, gems: 0  }, // day 20
  { stars:  5000, ash:  300, gems: 8  }, // day 21 ★GEM
  { stars:  800,  ash:  0,   gems: 0  }, // day 22
  { stars:  0,    ash:  120, gems: 0  }, // day 23
  { stars:  1500, ash:  0,   gems: 0  }, // day 24
  { stars:  0,    ash:  180, gems: 0  }, // day 25
  { stars:  2000, ash:  0,   gems: 0  }, // day 26
  { stars:  1500, ash:  240, gems: 0  }, // day 27
  { stars:  6000, ash:  400, gems: 10 }, // day 28 ★GEM
  { stars:  1000, ash:  0,   gems: 0  }, // day 29
  { stars:  0,    ash:  160, gems: 0  }, // day 30
  { stars:  1800, ash:  0,   gems: 0  }, // day 31
  { stars:  0,    ash:  220, gems: 0  }, // day 32
  { stars:  2500, ash:  0,   gems: 0  }, // day 33
  { stars:  2000, ash:  300, gems: 0  }, // day 34
  { stars:  8000, ash:  500, gems: 12 }, // day 35 ★GEM
  { stars:  1200, ash:  0,   gems: 0  }, // day 36
  { stars:  0,    ash:  180, gems: 0  }, // day 37
  { stars:  2000, ash:  0,   gems: 0  }, // day 38
  { stars:  0,    ash:  260, gems: 0  }, // day 39
  { stars:  3000, ash:  0,   gems: 0  }, // day 40
  { stars:  2500, ash:  360, gems: 0  }, // day 41
  { stars: 10000, ash:  600, gems: 15 }, // day 42 ★GEM
  { stars:  1500, ash:  0,   gems: 0  }, // day 43
  { stars:  0,    ash:  200, gems: 0  }, // day 44
  { stars:  2500, ash:  0,   gems: 0  }, // day 45
  { stars:  0,    ash:  300, gems: 0  }, // day 46
  { stars:  3500, ash:  0,   gems: 0  }, // day 47
  { stars:  3000, ash:  400, gems: 0  }, // day 48
  { stars: 12000, ash:  700, gems: 18 }, // day 49 ★GEM
  { stars:  3000, ash:  300, gems: 0  }, // day 50
  { stars:  0,    ash:  240, gems: 0  }, // day 51
  { stars:  3000, ash:  0,   gems: 0  }, // day 52
  { stars:  0,    ash:  340, gems: 0  }, // day 53
  { stars:  4000, ash:  0,   gems: 0  }, // day 54
  { stars:  3500, ash:  500, gems: 0  }, // day 55
  { stars: 15000, ash:  800, gems: 20 }, // day 56 ★GEM
  { stars:  2000, ash:  0,   gems: 0  }, // day 57
  { stars:  0,    ash:  260, gems: 0  }, // day 58
  { stars:  3000, ash:  0,   gems: 0  }, // day 59
  { stars:  0,    ash:  360, gems: 0  }, // day 60
  { stars:  4000, ash:  0,   gems: 0  }, // day 61
  { stars:  3500, ash:  500, gems: 0  }, // day 62
  { stars: 12000, ash:  800, gems: 22 }, // day 63 ★GEM
  { stars:  2200, ash:  0,   gems: 0  }, // day 64
  { stars:  0,    ash:  280, gems: 0  }, // day 65
  { stars:  3500, ash:  0,   gems: 0  }, // day 66
  { stars:  0,    ash:  400, gems: 0  }, // day 67
  { stars:  5000, ash:  0,   gems: 0  }, // day 68
  { stars:  4000, ash:  600, gems: 0  }, // day 69
  { stars: 14000, ash: 1000, gems: 25 }, // day 70 ★GEM
  { stars:  2500, ash:  0,   gems: 0  }, // day 71
  { stars:  0,    ash:  300, gems: 0  }, // day 72
  { stars:  4000, ash:  0,   gems: 0  }, // day 73
  { stars:  0,    ash:  440, gems: 0  }, // day 74
  { stars:  6000, ash:  0,   gems: 0  }, // day 75
  { stars:  5000, ash:  700, gems: 0  }, // day 76
  { stars: 16000, ash: 1200, gems: 30 }, // day 77 ★GEM
  { stars:  3000, ash:  0,   gems: 0  }, // day 78
  { stars:  0,    ash:  320, gems: 0  }, // day 79
  { stars:  5000, ash:  0,   gems: 0  }, // day 80
  { stars:  0,    ash:  480, gems: 0  }, // day 81
  { stars:  7000, ash:  0,   gems: 0  }, // day 82
  { stars:  6000, ash:  800, gems: 0  }, // day 83
  { stars: 18000, ash: 1400, gems: 35 }, // day 84 ★GEM
  { stars:  3500, ash:  0,   gems: 0  }, // day 85
  { stars:  0,    ash:  360, gems: 0  }, // day 86
  { stars:  6000, ash:  0,   gems: 0  }, // day 87
  { stars:  0,    ash:  560, gems: 0  }, // day 88
  { stars:  8000, ash:  0,   gems: 0  }, // day 89
  { stars:  7000, ash: 1000, gems: 0  }, // day 90
  { stars: 20000, ash: 1600, gems: 40 }, // day 91 ★GEM (note: fixed dup from before)
  { stars:  4000, ash:  400, gems: 0  }, // day 92
  { stars:  0,    ash:  400, gems: 0  }, // day 93
  { stars:  8000, ash:  0,   gems: 0  }, // day 94
  { stars:  0,    ash:  600, gems: 0  }, // day 95
  { stars: 10000, ash:  0,   gems: 0  }, // day 96
  { stars:  8000, ash: 1200, gems: 0  }, // day 97 (was dup day 90)
  { stars: 12000, ash:  0,   gems: 0  }, // day 98
  { stars:  0,    ash: 1600, gems: 0  }, // day 99
  { stars: 75000, ash: 8000, gems: 100}, // day 100 ★MEGA REWARD
];

// ==========================================
// MAIN APP COMPONENT
// ==========================================
function App() {

  // ── State ─────────────────────────────────────────────────
  const [user,             setUser]             = useState(null);
  const [activeTab,        setActiveTab]        = useState('PACKS');
  const [statsSubTab,      setStatsSubTab]      = useState('cards');   // 'cards' | 'foils' | 'collection'
  const [isOpening,        setIsOpening]        = useState(false);
  const [pulledCards,      setPulledCards]      = useState([]);
  // eslint-disable-next-line no-unused-vars
  const [leaderboard,      setLeaderboard]      = useState([]);
  const [market,           setMarket]           = useState([]);
  const [eventActive,      setEventActive]      = useState(false);
  const [isAdminOpen,      setIsAdminOpen]      = useState(false);
  const [isMaintenance,    setIsMaintenance]    = useState(false);
  const [broadcastMsg,     setBroadcastMsg]     = useState('');
  const [currentBroadcast, setCurrentBroadcast] = useState('');
  const [offlineData,      setOfflineData]      = useState(null);
  const [showSavePulse,    setShowSavePulse]    = useState(false);
  const [localStars,       setLocalStars]       = useState(0);
  const [localAsh,         setLocalAsh]         = useState(0);
  const [localGems,        setLocalGems]        = useState(0);
  const [vaultInput,       setVaultInput]       = useState('');
  const [vaultCurrency,    setVaultCurrency]    = useState('stars');
  const [fusionTarget,     setFusionTarget]     = useState(null);
  const [showAshModal,     setShowAshModal]     = useState(null);
  const [shopSubTab,       setShopSubTab]       = useState('daily');
  const [collectionSearch, setCollectionSearch] = useState('');
  const [marketSearch,     setMarketSearch]     = useState('');
  const [bpCardModal,      setBpCardModal]      = useState(null); // card name to show after BP claim
  const [showLinkModal,    setShowLinkModal]    = useState(false);
  const [linkEmail,        setLinkEmail]        = useState('');
  const [linkPassword,     setLinkPassword]     = useState('');
  const [linkError,        setLinkError]        = useState('');
  const [linkSuccess,      setLinkSuccess]      = useState(false);
  const [linkMode,         setLinkMode]         = useState('link'); // 'link' | 'signin'

  const [playerData, setPlayerData] = useState({
    bio: 'Explorer', stars: 500, level: 1, lPoints: 0,
    ash: 0, gems: 0, vault: 0, ashVault: 0, gemVault: 0, inventory: [],
    bpMonthKey: '', bpBasePower: 0, bpClaimed: [],
    lastDailyClaimMs: 0, dailyStreak: 0,
    upgrades: { cap: 0, speed: 0, luck: 0, offline: 0 },
    costReductionTier: 0,
    lastOnline: Date.now(),
  });

  const [stats, setStats] = useState({
    totalUsers: 0, adsWatched: 0, estRevenue: 0, estFirebaseCost: 0,
  });

  const isInitialLoad = useRef(true);
  const starsRef      = useRef(0);
  const ashRef        = useRef(0);
  const gemsRef       = useRef(0);
  const prevLevelRef  = useRef(1);

  // ── Derived ───────────────────────────────────────────────
  const totalPower = (playerData.inventory || []).reduce((sum, entry) => {
    return sum + getEntryPower(entry);
  }, 0);

  const { level, nextReq, progress } = calcLevel(totalPower);
  const maxStars     = 2000 + ((playerData.upgrades?.cap || 0) * 500);
  const baseInterval = Math.max(1000 - ((playerData.upgrades?.speed || 0) * 10), 100);
  const starInterval = eventActive ? baseInterval / 2 : baseInterval;
  const hasDivine    = pulledCards.some(c => c.rarity === 'Divine');
  const vaultStars   = playerData.vault    || 0;
  const vaultAsh     = playerData.ashVault || 0;
  const vaultGems    = playerData.gemVault || 0;

  // Battle pass
  const monthlyGain       = Math.max(0, totalPower - (playerData.bpBasePower || 0));
  const unlockedTierCount = BP_TIERS.filter(t => monthlyGain >= t.powerReq).length;
  const bpClaimed         = playerData.bpClaimed || [];

  // Daily reward
  const now          = Date.now();
  const todayStr     = new Date(now).toDateString();
  const lastClaimMs  = playerData.lastDailyClaimMs || 0;
  const lastClaimStr = lastClaimMs ? new Date(lastClaimMs).toDateString() : '';
  const yesterday    = new Date(now - 86400000).toDateString();
  const dailyCanClaim   = todayStr !== lastClaimStr;
  const streakBroken    = lastClaimMs > 0 && lastClaimStr !== todayStr && lastClaimStr !== yesterday;
  const dailyStreak     = streakBroken ? 0 : (playerData.dailyStreak || 0);
  const streakIndex     = Math.min(dailyStreak, DAILY_REWARDS.length - 1);
  const todayReward     = DAILY_REWARDS[streakIndex];

  // Collection stats
  const ownedBaseNames = new Set(
    (playerData.inventory || []).map(e => parseInvEntry(e).name)
  );
  const collectableCards = CARD_LIBRARY.filter(c => c.pool !== 'battlepass');
  const totalCollectable = collectableCards.length;
  const totalOwned       = collectableCards.filter(c => ownedBaseNames.has(c.name)).length;

  // ── Level-up → award points ────────────────────────────────
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!user || isInitialLoad.current) return;
    const prev = prevLevelRef.current;
    if (level > prev) {
      const gained    = level - prev;
      const newPoints = (playerData.lPoints || 0) + gained;
      prevLevelRef.current = level;
      updateDoc(doc(db, 'users', user.uid), { lPoints: newPoints, level });
    } else if (level < prev) {
      prevLevelRef.current = level;
      updateDoc(doc(db, 'users', user.uid), { level });
    }
  }, [level, user]);

  // ── Auth & Setup ──────────────────────────────────────────
  // Track the last UID we ran setupUser for. This lets us correctly
  // handle user switches (anon → email sign-in) while avoiding
  // double-calling setupUser for the same user.
  const lastSetupUidRef = useRef(null);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, u => {
      if (u) {
        if (u.uid !== lastSetupUidRef.current) {
          // New or switched user — reset load flags and run setup
          lastSetupUidRef.current = u.uid;
          isInitialLoad.current   = true;
          setUser(u);
          setupUser(u);
        } else {
          // Same user (e.g. token refresh) — just update state, don't re-setup
          setUser(u);
        }
      } else {
        // No session at all — sign in anonymously
        lastSetupUidRef.current = null;
        signInAnonymously(auth).catch(err => console.error('Anon auth error:', err));
      }
    });
    return () => unsub();
  }, []);

  const setupUser = async (u) => {
    const userRef  = doc(db, 'users', u.uid);
    const userSnap = await getDoc(userRef);
    if (!userSnap.exists()) {
      await setDoc(userRef, {
        bio: 'Explorer', stars: 500, level: 1, lPoints: 0,
        ash: 0, gems: 0, vault: 0, ashVault: 0, gemVault: 0,
        uid: u.uid, inventory: [],
        bpMonthKey: new Date().toISOString().slice(0, 7),
        bpBasePower: 0, bpClaimed: [],
        lastDailyClaimMs: 0, dailyStreak: 0,
        upgrades: { cap: 0, speed: 0, luck: 0, offline: 0 },
        costReductionTier: 0,
        lastOnline: Date.now(),
      });
      setLocalStars(500);
      await updateDoc(doc(db, 'metadata', 'stats'), { userCount: increment(1) });
    } else {
      const data = userSnap.data();
      setLocalStars(Math.min(data.stars || 0, FIELD_CAPS.stars));
      setLocalAsh(Math.min(data.ash   || 0, FIELD_CAPS.ash));
      setLocalGems(Math.min(data.gems || 0, FIELD_CAPS.gems));
      const secondsAway = Math.floor((Date.now() - (data.lastOnline || Date.now())) / 1000);
      if (secondsAway >= 60) {
        const gained = Math.floor(secondsAway * (0.1 + (data.upgrades?.offline || 0) * 0.1));
        if (gained > 0) setOfflineData({ stars: gained, time: secondsAway });
      }
    }
  };

  // ── Firebase listeners ────────────────────────────────────
  useEffect(() => {
    if (!user) return;

    const unsubUser = onSnapshot(doc(db, 'users', user.uid), snap => {
      if (!snap.exists()) return;
      const raw  = snap.data();
      const data = sanitizePlayerData(raw);

      const hasCheat = Object.keys(FIELD_CAPS).some(f =>
        typeof raw[f] === 'number' && (raw[f] > FIELD_CAPS[f] || raw[f] < 0)
      );
      if (hasCheat) {
        updateDoc(doc(db, 'users', user.uid), {
          stars: data.stars, ash: data.ash, gems: data.gems,
          vault: data.vault, ashVault: data.ashVault, gemVault: data.gemVault,
          lPoints: data.lPoints, level: data.level, inventory: data.inventory,
        });
      }

      setPlayerData(data);
      if (isInitialLoad.current) {
        setLocalStars(data.stars || 0);
        setLocalAsh(data.ash   || 0);
        setLocalGems(data.gems || 0);
        prevLevelRef.current = data.level || 1;
        isInitialLoad.current = false;

        const monthKey = new Date().toISOString().slice(0, 7);
        if ((data.bpMonthKey || '') !== monthKey) {
          const power = (data.inventory || []).reduce((s, e) => s + getEntryPower(e), 0);
          updateDoc(doc(db, 'users', user.uid), {
            bpMonthKey: monthKey, bpBasePower: power, bpClaimed: [],
          });
        }
      }
    });

    const unsubMkt = onSnapshot(
      query(collection(db, 'market'), orderBy('timestamp', 'desc'), limit(20)),
      snap => setMarket(snap.docs.map(d => ({ id: d.id, ...d.data() })))
    );

    const unsubEvent = onSnapshot(doc(db, 'global_events', 'current'), snap => {
      if (snap.exists()) {
        setEventActive(snap.data().active || false);
        setIsMaintenance(snap.data().maintenance || false);
      }
    });

    const unsubBroadcast = onSnapshot(doc(db, 'global_events', 'broadcast'), snap => {
      if (snap.exists()) setCurrentBroadcast(snap.data().text || '');
    });

    return () => { unsubUser(); unsubMkt(); unsubEvent(); unsubBroadcast(); };
  }, [user]);

  useEffect(() => {
    if (activeTab !== 'RANKING' || !user) return;
    getDocs(query(collection(db, 'users'), orderBy('level', 'desc'), limit(10)))
      .then(snap => setLeaderboard(snap.docs.map(d => ({ id: d.id, ...d.data() }))));
  }, [activeTab, user]);

  // ── Game loop ─────────────────────────────────────────────
  useEffect(() => { starsRef.current = localStars; }, [localStars]);
  useEffect(() => { ashRef.current   = localAsh;   }, [localAsh]);
  useEffect(() => { gemsRef.current  = localGems;  }, [localGems]);

  useEffect(() => {
    if (!user || isMaintenance) return;
    const regenTimer = setInterval(() => {
      if (starsRef.current < maxStars)
        setLocalStars(prev => Math.min(prev + 1, maxStars));
    }, starInterval);
    const syncTimer = setInterval(() => {
      updateDoc(doc(db, 'users', user.uid), {
        stars: Math.min(starsRef.current, FIELD_CAPS.stars),
        ash:   Math.min(ashRef.current,   FIELD_CAPS.ash),
        gems:  Math.min(gemsRef.current,  FIELD_CAPS.gems),
        lastOnline: Date.now(),
      });
      triggerSavePulse();
    }, 20000);
    return () => { clearInterval(regenTimer); clearInterval(syncTimer); };
  }, [user, starInterval, maxStars, isMaintenance]);

  const triggerSavePulse = () => {
    setShowSavePulse(true);
    setTimeout(() => setShowSavePulse(false), 2000);
  };

  // ── Roll card — pool-aware + foil roll ────────────────────
  const rollCard = (pack) => {
    const pool      = pack?.pool || 'standard';
    const rules     = pack?.rarityRules || { divine: 0.1, mythic: 1.5, legendary: 25, epic: 120, rare: 350 };
    const luckLevel = playerData.upgrades?.luck || 0;
    const roll      = Math.random() * 1000;

    let rarity;
    if      (roll <= (rules.divine    || 0))                 rarity = 'Divine';
    else if (roll <= (rules.mythic    || 0) + luckLevel)     rarity = 'Mythic';
    else if (roll <= (rules.legendary || 0) + luckLevel * 3) rarity = 'Legendary';
    else if (roll <= (rules.epic      || 0))                  rarity = 'Epic';
    else if (roll <= (rules.rare      || 350))                rarity = 'Rare';
    else                                                       rarity = 'Common';

    if (eventActive && pool === 'standard' && rarity === 'Mythic' && Math.random() > 0.5) {
      const nebula = CARD_LIBRARY.find(c => c.name === 'Nebula Kraken' && c.pool === 'standard');
      if (nebula) {
        const foil = rollFoil();
        return { ...nebula, foil, invEntry: buildInvEntry(nebula.name, foil) };
      }
    }

    let cardPool = CARD_LIBRARY.filter(c => c.rarity === rarity && c.pool === pool);
    if (cardPool.length === 0) {
      for (let i = RARITY_FALLBACK.indexOf(rarity) + 1; i < RARITY_FALLBACK.length; i++) {
        cardPool = CARD_LIBRARY.filter(c => c.rarity === RARITY_FALLBACK[i] && c.pool === pool);
        if (cardPool.length > 0) break;
      }
    }

    const card = cardPool.length > 0
      ? cardPool[Math.floor(Math.random() * cardPool.length)]
      : (CARD_LIBRARY.find(c => c.pool === pool) || CARD_LIBRARY[0]);

    // Roll foil independently — luck does NOT affect this
    const foil = rollFoil();
    return { ...card, foil, invEntry: buildInvEntry(card.name, foil) };
  };

  // ── Open pack ─────────────────────────────────────────────
  const openPack = async (pid) => {
    const p = Object.values(PACK_TYPES).find(pack => pack.id === pid);
    if (!p) return;
    const currency = p.currency || 'stars';
    const discountTier = playerData.costReductionTier || 0;
    const discountedCost = getDiscountedPrice(p.cost, discountTier);
    
    if (currency === 'stars') {
      if (localStars < discountedCost) return alert('NOT ENOUGH STARS!');
      setLocalStars(prev => prev - discountedCost);
    } else if (currency === 'ash') {
      if (localAsh < discountedCost) return alert(`NEED ${discountedCost} ASH!`);
      setLocalAsh(prev => prev - discountedCost);
    }
    setIsOpening(true);
    const pulls  = Array.from({ length: p.cardCount }, () => rollCard(p));
    const newInv = [...playerData.inventory, ...pulls.map(c => c.invEntry)];
    const updateData = { inventory: newInv };
    if (currency === 'stars') updateData.stars = Math.max(0, starsRef.current - discountedCost);
    if (currency === 'ash')   updateData.ash   = Math.max(0, ashRef.current  - discountedCost);
    await updateDoc(doc(db, 'users', user.uid), updateData);
    setTimeout(() => { setIsOpening(false); setPulledCards(pulls); }, 1200);
  };

  // ── Open ad pack ──────────────────────────────────────────
  const openAdPack = (pid) => {
    const p = PACK_TYPES[pid];
    if (!p || p.currency !== 'ad') return;
    watchRewardedAd(async () => {
      setIsOpening(true);
      const pulls  = Array.from({ length: p.cardCount }, () => rollCard(p));
      const newInv = [...playerData.inventory, ...pulls.map(c => c.invEntry)];
      await updateDoc(doc(db, 'users', user.uid), { inventory: newInv });
      setTimeout(() => { setIsOpening(false); setPulledCards(pulls); }, 1200);
    });
  };

  // ── Fusion ────────────────────────────────────────────────
  // FIX: deduct 10 cards IMMEDIATELY when spin starts (via onDeduct callback).
  // "Fuse Again" re-spins but calls onDeduct again, so it costs 10 more copies.
  const openFusionCase = (cardName, count) => setFusionTarget({ cardName, count });

  // Called by FusionCase at the START of each spin — deducts 10 copies
  const handleFusionDeduct = async (cardName) => {
    let removed = 0;
    const filtered = playerData.inventory.filter(entry => {
      const { name } = parseInvEntry(entry);
      // Only remove non-foil copies for fusion cost
      if (name === cardName && removed < 10 && !parseInvEntry(entry).foil) {
        removed++;
        return false;
      }
      return true;
    });
    if (removed < 10) return false; // not enough copies
    await updateDoc(doc(db, 'users', user.uid), { inventory: filtered, stars: localStars });
    return true;
  };

  // Called by FusionCase when player clicks "COLLECT REWARD"
  const handleFusionCollect = async (rewardCard) => {
    setFusionTarget(null);
    const validCard = CARD_LIBRARY.find(c => c.name === rewardCard.name);
    if (!validCard) return;
    // Fusion rewards NEVER get foil
    const newEntry = validCard.name;
    await updateDoc(doc(db, 'users', user.uid), {
      inventory: [...playerData.inventory, newEntry],
    });
    setPulledCards([{ ...validCard, foil: null, invEntry: newEntry }]);
  };

  // ── Ashing ────────────────────────────────────────────────
  const ASH_CONFIRM_RARITIES = new Set(['Legendary', 'Mythic', 'Divine', 'Special']);

  const doAsh = async (invEntry) => {
    const { name, foil } = parseInvEntry(invEntry);
    const card    = CARD_LIBRARY.find(c => c.name === name);
    if (!card) return;
    const basAsh   = ASH_VALUES[card.rarity] || 2;
    const foilMult = foil ? (FOIL_TYPES[foil]?.multiplier || 1) : 1;
    const ashGain  = Math.floor(basAsh * foilMult);
    const inventory = [...playerData.inventory];
    const idx = inventory.indexOf(invEntry);
    if (idx === -1) return;
    inventory.splice(idx, 1);
    const newAsh = Math.min(ashRef.current + ashGain, FIELD_CAPS.ash);
    setLocalAsh(newAsh);
    await updateDoc(doc(db, 'users', user.uid), { inventory, ash: newAsh });
    triggerSavePulse();
  };

  const handleAshCard = async () => {
    if (!showAshModal) return;
    const { invEntry } = showAshModal;
    setShowAshModal(null);
    await doAsh(invEntry);
  };

  // Called directly from card button — skips confirm for Common/Rare/Epic
  const handleAshBtn = (invEntry, rarity) => {
    if (ASH_CONFIRM_RARITIES.has(rarity)) {
      const { name } = parseInvEntry(invEntry);
      setShowAshModal({ invEntry, cardName: name });
    } else {
      doAsh(invEntry);
    }
  };

  // ── Daily reward ──────────────────────────────────────────
  const handleDailyClaim = async () => {
    if (!dailyCanClaim) return;
    const reward    = DAILY_REWARDS[streakIndex];
    const newStars  = Math.min(localStars + (reward.stars || 0), FIELD_CAPS.stars);
    const newAsh    = Math.min(localAsh   + (reward.ash   || 0), FIELD_CAPS.ash);
    const newGems   = Math.min(localGems  + (reward.gems  || 0), FIELD_CAPS.gems);
    const newStreak = streakBroken ? 1 : dailyStreak + 1;
    setLocalStars(newStars);
    setLocalAsh(newAsh);
    setLocalGems(newGems);
    await updateDoc(doc(db, 'users', user.uid), {
      stars: newStars, ash: newAsh, gems: newGems,
      lastDailyClaimMs: Date.now(), dailyStreak: newStreak,
    });
    triggerSavePulse();
  };

  // ── Battle pass claim ─────────────────────────────────────
  const handleBPClaim = async (tierIdx) => {
    if (bpClaimed.includes(tierIdx) || tierIdx >= unlockedTierCount) return;
    const tier       = BP_TIERS[tierIdx];
    const newStars   = Math.min(localStars + (tier.stars || 0), FIELD_CAPS.stars);
    const newAsh     = Math.min(localAsh   + (tier.ash   || 0), FIELD_CAPS.ash);
    const newGems    = Math.min(localGems  + (tier.gems  || 0), FIELD_CAPS.gems);
    const newClaimed = [...bpClaimed, tierIdx];
    const updateData = {
      stars: newStars, ash: newAsh, gems: newGems, bpClaimed: newClaimed,
    };
    // Add card reward to inventory if present
    let cardRewardName = null;
    if (tier.cardReward) {
      const bpCard = CARD_LIBRARY.find(c => c.name === tier.cardReward);
      if (bpCard) {
        cardRewardName = bpCard.name;
        updateData.inventory = [...playerData.inventory, bpCard.name];
      }
    }
    setLocalStars(newStars);
    setLocalAsh(newAsh);
    setLocalGems(newGems);
    await updateDoc(doc(db, 'users', user.uid), updateData);
    if (cardRewardName) setBpCardModal(cardRewardName);
    triggerSavePulse();
  };

  // ── Upgrades ──────────────────────────────────────────────
  const buyUpgrade = async (type) => {
    if ((playerData.lPoints || 0) < 1)
      return alert('NOT ENOUGH LEVEL POINTS! EARN THEM BY LEVELLING UP.');
    const newPoints   = Math.max(0, (playerData.lPoints || 0) - 1);
    const newUpgrades = { ...playerData.upgrades, [type]: (playerData.upgrades?.[type] || 0) + 1 };
    await updateDoc(doc(db, 'users', user.uid), {
      lPoints: newPoints, upgrades: newUpgrades, stars: localStars,
    });
  };

  // ── Cost Reduction Upgrade Purchase ─────────────────────
  const buyCostReductionUpgrade = async (tier) => {
    const upgrade = COST_REDUCTION_UPGRADES.find(u => u.tier === tier);
    if (!upgrade) return;
    
    const currentTier = playerData.costReductionTier || 0;
    if (tier !== currentTier + 1) {
      return alert('You must purchase previous tiers first!');
    }
    
    if (localGems < upgrade.cost) {
      return alert(`NEED ${upgrade.cost.toLocaleString()} 💎!`);
    }
    
    setLocalGems(prev => prev - upgrade.cost);
    await updateDoc(doc(db, 'users', user.uid), {
      costReductionTier: tier,
      gems: Math.max(0, gemsRef.current - upgrade.cost),
    });
    triggerSavePulse();
  };

  // ── Market ────────────────────────────────────────────────
  const buyFromMarket = async (item) => {
    if (localGems < item.price)     return alert(`NOT ENOUGH GEMS — NEED ${item.price} 💎`);
    if (item.sellerId === user.uid) return alert('YOU CANNOT BUY YOUR OWN CARD');
    const { name } = parseInvEntry(item.cardEntry || item.cardName);
    if (!CARD_LIBRARY.find(c => c.name === name)) return alert('INVALID CARD');
    const newGems   = localGems - item.price;
    setLocalGems(newGems);
    // Seller receives gems
    const sellerRef  = doc(db, 'users', item.sellerId);
    const sellerSnap = await getDoc(sellerRef);
    if (sellerSnap.exists())
      await updateDoc(sellerRef, { gems: Math.min((sellerSnap.data().gems || 0) + item.price, FIELD_CAPS.gems) });
    await updateDoc(doc(db, 'users', user.uid), {
      gems: newGems, inventory: [...playerData.inventory, item.cardEntry || item.cardName],
    });
    await deleteDoc(doc(db, 'market', item.id));
    triggerSavePulse();
  };

  const handleAdReward = async () => {
    const newStars = Math.min(starsRef.current + 1000, FIELD_CAPS.stars);
    setLocalStars(newStars);
    await updateDoc(doc(db, 'global_events', 'stats'), {
      totalAdsWatched: increment(1), totalRevenue: increment(0.01),
    });
    await updateDoc(doc(db, 'users', user.uid), { stars: newStars });
  };

  const handleSellCard = async (invEntry) => {
    const { name } = parseInvEntry(invEntry);
    if (!CARD_LIBRARY.find(c => c.name === name)) return;
    const price = prompt('SET PRICE (💎 GEMS):');
    if (!price || isNaN(price) || parseInt(price) <= 0) return;
    await addDoc(collection(db, 'market'), {
      sellerId:   user.uid,
      sellerName: playerData.bio,
      cardName:   name,
      cardEntry:  invEntry,
      price:      Math.min(parseInt(price), FIELD_CAPS.gems), // price in gems
      currency:   'gems',
      timestamp:  Date.now(),
    });
    const inventory = [...playerData.inventory];
    const idx = inventory.indexOf(invEntry);
    if (idx > -1) {
      inventory.splice(idx, 1);
      await updateDoc(doc(db, 'users', user.uid), { inventory });
      alert(`${name} HAS BEEN LISTED.`);
    }
  };

  // ── Vault — stars, ash AND gems ───────────────────────────
  const getVaultValues = () => {
    if (vaultCurrency === 'stars') return { wallet: localStars, vault: vaultStars };
    if (vaultCurrency === 'ash')   return { wallet: localAsh,   vault: vaultAsh   };
    return                                { wallet: localGems,  vault: vaultGems  };
  };
  const handleVaultDeposit = async () => {
    const amount = parseInt(vaultInput);
    if (!amount || amount <= 0) return alert('ENTER A VALID AMOUNT.');
    const { wallet } = getVaultValues();
    if (amount > wallet) return alert('NOT ENOUGH TO DEPOSIT.');
    setVaultInput('');
    if (vaultCurrency === 'stars') {
      const ns = localStars - amount;
      setLocalStars(ns);
      await updateDoc(doc(db, 'users', user.uid), { stars: ns, vault: vaultStars + amount });
    } else if (vaultCurrency === 'ash') {
      const na = localAsh - amount;
      setLocalAsh(na);
      await updateDoc(doc(db, 'users', user.uid), { ash: na, ashVault: vaultAsh + amount });
    } else {
      const ng = localGems - amount;
      setLocalGems(ng);
      await updateDoc(doc(db, 'users', user.uid), { gems: ng, gemVault: vaultGems + amount });
    }
    triggerSavePulse();
  };
  const handleVaultWithdraw = async () => {
    const amount = parseInt(vaultInput);
    if (!amount || amount <= 0) return alert('ENTER A VALID AMOUNT.');
    const { vault } = getVaultValues();
    if (amount > vault) return alert('NOT ENOUGH IN VAULT.');
    setVaultInput('');
    if (vaultCurrency === 'stars') {
      const ns = Math.min(localStars + amount, FIELD_CAPS.stars);
      setLocalStars(ns);
      await updateDoc(doc(db, 'users', user.uid), { stars: ns, vault: vaultStars - amount });
    } else if (vaultCurrency === 'ash') {
      const na = Math.min(localAsh + amount, FIELD_CAPS.ash);
      setLocalAsh(na);
      await updateDoc(doc(db, 'users', user.uid), { ash: na, ashVault: vaultAsh - amount });
    } else {
      const ng = Math.min(localGems + amount, FIELD_CAPS.gems);
      setLocalGems(ng);
      await updateDoc(doc(db, 'users', user.uid), { gems: ng, gemVault: vaultGems - amount });
    }
    triggerSavePulse();
  };

  // ── Admin ─────────────────────────────────────────────────
  const updateProjectStats = async () => {
    const [statsSnap, globalSnap] = await Promise.all([
      getDoc(doc(db, 'metadata', 'stats')),
      getDoc(doc(db, 'global_events', 'stats')),
    ]);
    const userCount  = statsSnap.exists()  ? statsSnap.data().userCount  || 0 : 0;
    const globalData = globalSnap.exists() ? globalSnap.data()            : { totalRevenue: 0, totalAdsWatched: 0 };
    const readCost   = ((userCount * 15) / 100000) * 0.06;
    const writeCost  = ((userCount * 5)  / 100000) * 0.18;
    setStats({
      totalUsers: userCount, adsWatched: globalData.totalAdsWatched || 0,
      estRevenue: globalData.totalRevenue || 0,
      estFirebaseCost: (readCost + writeCost + 0.01).toFixed(4),
    });
  };

  // ── Pack buckets ──────────────────────────────────────────
  const starPacks = Object.values(PACK_TYPES).filter(p => p.currency === 'stars');
  const ashPacks  = Object.values(PACK_TYPES).filter(p => p.currency === 'ash');
  const adPacks   = Object.values(PACK_TYPES).filter(p => p.currency === 'ad');

  // ── Helpers for card grid ─────────────────────────────────
  // Group inventory entries by base name + foil, return display objects
  const getGroupedInventory = () => {
    const groups = {};
    for (const entry of (playerData.inventory || [])) {
      const { name, foil } = parseInvEntry(entry);
      const key = entry; // unique by full entry
      if (!groups[key]) {
        const card = CARD_LIBRARY.find(c => c.name === name);
        const power = getEntryPower(entry);
        groups[key] = { invEntry: entry, name, foil, card, power, count: 0 };
      }
      groups[key].count++;
    }
    return Object.values(groups).sort((a, b) => {
      const ra = RARITY_ORDER[a.card?.rarity] || 99;
      const rb = RARITY_ORDER[b.card?.rarity] || 99;
      if (ra !== rb) return ra - rb;
      if (a.foil && !b.foil) return -1;
      if (!a.foil && b.foil) return 1;
      return 0;
    });
  };

  // ==========================================
  // RENDER
  // ==========================================
  if (isMaintenance && user?.uid !== ADMIN_UID) {
    return (
      <div className="maintenance-screen">
        <div className="m-content">
          <h1>UNDER MAINTENANCE</h1>
          <p>INFNOTES IS CURRENTLY UPDATING. CHECK BACK LATER.</p>
        </div>
      </div>
    );
  }

  const { wallet: vWallet, vault: vVault } = getVaultValues();
  const groupedInv = getGroupedInventory();
  const foilOnly   = groupedInv.filter(g => g.foil);
  const normalOnly = groupedInv.filter(g => !g.foil);

  const filteredCollection = normalOnly.filter(g =>
    g.name.toLowerCase().includes(collectionSearch.toLowerCase())
  );
  const filteredFoils = foilOnly.filter(g =>
    g.name.toLowerCase().includes(collectionSearch.toLowerCase())
  );
  const filteredMarket = market.filter(item =>
    item.cardName.toLowerCase().includes(marketSearch.toLowerCase())
  );

  // Per-rarity collection stats
  const rarityStats = ['Divine','Mythic','Legendary','Epic','Rare','Common'].map(rarity => {
    const total = collectableCards.filter(c => c.rarity === rarity).length;
    const owned = collectableCards.filter(c => c.rarity === rarity && ownedBaseNames.has(c.name)).length;
    return { rarity, owned, total };
  });

  return (
    <div className={`app-dark ${eventActive ? 'event-mode' : ''}`} style={{ minHeight: '100vh', padding: '20px' }}>

      {currentBroadcast && (
        <div className="broadcast-banner"><div className="scroll-text">{currentBroadcast}</div></div>
      )}

      {/* ── HEADER ── */}
      <header>
        <div className="profile-section">
          <h1 onClick={() => {
            const n = prompt('ENTER NEW USERNAME:', playerData.bio);
            if (n && user) updateDoc(doc(db, 'users', user.uid), { bio: n });
          }}>
            {playerData.bio}
          </h1>
          <div className={`save-tag ${showSavePulse ? 'pulse' : ''}`}>SYNCED</div>
          {/* Account link button — shows email if linked, button if anonymous */}
          {user?.isAnonymous ? (
            <button className="link-account-btn" onClick={() => { setShowLinkModal(true); setLinkError(''); setLinkSuccess(false); setLinkEmail(''); setLinkPassword(''); setLinkMode('link'); }}
              title="Link account to email">
              🔗 SAVE ACCOUNT
            </button>
          ) : (
            <span className="linked-email-badge" title="Account linked">
              ✓ {user?.email}
            </span>
          )}
          {user?.uid === ADMIN_UID && (
            <button className="admin-toggle-btn" onClick={() => setIsAdminOpen(true)}>⚙️</button>
          )}
        </div>
        <div className="currency-display">
          <span className="hdr-stars">{localStars.toLocaleString()} ⭐</span>
          <span className="hdr-ash">{localAsh.toLocaleString()} ASH</span>
          <span className="hdr-gems">{localGems.toLocaleString()} 💎</span>
        </div>
      </header>

      {/* ── NAV ── */}
      <nav className="tab-bar">
        {['PACKS', 'STATS', 'VAULT', 'SHOP', 'RANKING'].map(tab => (
          <button
            key={tab}
            className={activeTab === tab ? 'active' : ''}
            onClick={() => setActiveTab(tab)}
          >
            {tab}
          </button>
        ))}
      </nav>

      <main className="content">

        {/* ════ PACKS ════ */}
        {activeTab === 'PACKS' && (
          <div className="tab-fade">
            <div className="pack-section-label">STAR PACKS</div>
            <div className="pack-container">
              {starPacks.map(p => {
                const discountTier = playerData.costReductionTier || 0;
                const discountedCost = getDiscountedPrice(p.cost, discountTier);
                return (
                <div key={p.id} className={`pack-card ${p.cssClass}`} onClick={() => openPack(p.id)}>
                  <div className="pack-art">{p.art}</div>
                  <h3>{p.displayName}</h3>
                  <p>
                    {discountedCost < p.cost && (
                      <>
                        <span style={{ textDecoration: 'line-through', opacity: 0.6 }}>{p.cost.toLocaleString()}</span>
                        {' '}
                      </>
                    )}
                    {discountedCost.toLocaleString()} ⭐
                  </p>
                </div>
              );
            })}
            </div>
            <div className="pack-section-label">
              ASH PACKS
              <span className="pack-section-balance">{localAsh.toLocaleString()} ASH</span>
            </div>
            <div className="pack-container">
              {ashPacks.map(p => (
                <div key={p.id} className={`pack-card ${p.cssClass}`} onClick={() => openPack(p.id)}>
                  <div className="pack-art">{p.art}</div>
                  <h3>{p.displayName}</h3>
                  <p>{p.cost.toLocaleString()} ASH</p>
                </div>
              ))}
            </div>
            <div className="pack-section-label">PROMO PACKS</div>
            <div className="pack-container">
              {adPacks.map(p => (
                <div key={p.id} className={`pack-card ${p.cssClass}`} onClick={() => openAdPack(p.id)}>
                  <div className="pack-art">{p.art}</div>
                  <h3>{p.displayName}</h3>
                  <p className="ad-pack-label">WATCH AD</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ════ STATS / INVENTORY ════ */}
        {activeTab === 'STATS' && (
          <div className="tab-fade">
            {/* Level header */}
            <div className="level-header">
              <div className="level-badge">
                <span className="label">LEVEL</span>
                <span className="level-num">{level}</span>
              </div>
              <div className="level-info">
                <div className="level-power-row">
                  <span className="level-power-label">TOTAL POWER</span>
                  <span className="level-power-val">{totalPower.toLocaleString()} / {nextReq.toLocaleString()}</span>
                </div>
                <div className="level-bar-bg">
                  <div className="level-bar-fill" style={{ width: `${(progress * 100).toFixed(1)}%` }} />
                </div>
                <div className="level-sub">
                  {progress < 1
                    ? `${(nextReq - totalPower).toLocaleString()} PWR to level ${level + 1}`
                    : 'LEVELING UP...'}
                </div>
                <div className="level-points-badge">{playerData.lPoints || 0} LEVEL POINTS</div>
              </div>
            </div>

            {/* Stats sub-tabs */}
            <div className="stats-sub-tabs">
              {[
                { key: 'cards',      label: `CARDS (${playerData.inventory?.length || 0})` },
                { key: 'foils',      label: `FOILS (${foilOnly.length})` },
                { key: 'collection', label: 'COLLECTION' },
              ].map(({ key, label }) => (
                <button
                  key={key}
                  className={`stats-sub-btn ${statsSubTab === key ? 'active' : ''}`}
                  onClick={() => setStatsSubTab(key)}
                >
                  {label}
                </button>
              ))}
            </div>

            {/* Search bar for cards/foils */}
            {(statsSubTab === 'cards' || statsSubTab === 'foils') && (
              <div className="search-bar-wrap">
                <input
                  className="search-input"
                  type="text"
                  placeholder="SEARCH CARDS..."
                  value={collectionSearch}
                  onChange={e => setCollectionSearch(e.target.value)}
                />
                {collectionSearch && (
                  <button className="search-clear" onClick={() => setCollectionSearch('')}>✕</button>
                )}
              </div>
            )}

            {/* Cards tab */}
            {statsSubTab === 'cards' && (
              <div className="portfolio-grid">
                {filteredCollection.map(({ invEntry, name, foil, card, power, count }) => {
                  const ashVal = Math.floor((ASH_VALUES[card?.rarity] || 2) * (foil ? FOIL_TYPES[foil]?.multiplier || 1 : 1));
                  // Count non-foil copies for fusion eligibility
                  const nonFoilCount = (playerData.inventory || []).filter(e => {
                    const p = parseInvEntry(e);
                    return p.name === name && !p.foil;
                  }).length;
                  return (
                    <div key={invEntry} className={`game-card ${card?.rarity?.toLowerCase() || 'common'}`}>
                      <div className="card-top">
                        <span className="rarity-label">{card?.rarity}</span>
                        <span className="card-count">x{count}</span>
                      </div>
                      <h3 className="card-title">{name}</h3>
                      <p className="card-pwr">+{power.toLocaleString()} PWR</p>
                      <div className="card-footer">
                        <button
                          className="fusion-btn"
                          onClick={() => openFusionCase(name, nonFoilCount)}
                          disabled={nonFoilCount < 10}
                        >
                          {nonFoilCount >= 10 ? 'OPEN FUSION CASE' : `FUSE (${nonFoilCount}/10)`}
                        </button>
                        <div className="card-bottom-row">
                          <button className="ash-btn" onClick={() => handleAshBtn(invEntry, card?.rarity)}>
                            ASH +{ashVal}
                          </button>
                          <button className="sell-btn" onClick={() => handleSellCard(invEntry)}>SELL</button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Foils tab */}
            {statsSubTab === 'foils' && (
              <div>
                {filteredFoils.length === 0 ? (
                  <p className="empty-msg">NO FOIL CARDS YET. FOILS DROP RANDOMLY FROM PACKS.</p>
                ) : (
                  <div className="portfolio-grid">
                    {filteredFoils.map(({ invEntry, name, foil, card, power, count }) => {
                      const foilConf = FOIL_TYPES[foil];
                      const ashVal   = Math.floor((ASH_VALUES[card?.rarity] || 2) * (foilConf?.multiplier || 1));
                      return (
                        <div key={invEntry} className={`game-card ${card?.rarity?.toLowerCase() || 'common'} foil-${foil}`}>
                          <div className="card-top">
                            <span className="rarity-label">{card?.rarity}</span>
                            <span className="foil-badge" style={{ color: foilConf?.color }}>
                              {foilConf?.label}
                            </span>
                            <span className="card-count">x{count}</span>
                          </div>
                          <h3 className="card-title">{name}</h3>
                          <p className="card-pwr">+{power.toLocaleString()} PWR <span className="foil-mult">×{foilConf?.multiplier}</span></p>
                          <div className="card-footer">
                            <div className="card-bottom-row">
                              <button className="ash-btn" onClick={() => handleAshBtn(invEntry, card?.rarity)}>
                                ASH +{ashVal}
                              </button>
                              <button className="sell-btn" onClick={() => handleSellCard(invEntry)}>SELL</button>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {/* Collection tracker */}
            {statsSubTab === 'collection' && (
              <div className="collection-tracker">
                <div className="collection-total">
                  <div className="col-total-num">{totalOwned} / {totalCollectable}</div>
                  <div className="col-total-label">CARDS COLLECTED</div>
                  <div className="col-total-bar-bg">
                    <div className="col-total-bar-fill" style={{ width: `${((totalOwned / totalCollectable) * 100).toFixed(1)}%` }} />
                  </div>
                  <div className="col-total-pct">{((totalOwned / totalCollectable) * 100).toFixed(1)}% COMPLETE</div>
                </div>
                <div className="collection-rarity-grid">
                  {rarityStats.map(({ rarity, owned, total }) => (
                    <div key={rarity} className={`col-rarity-card ${rarity.toLowerCase()}`}>
                      <div className="col-rar-label">{rarity}</div>
                      <div className="col-rar-count">{owned}/{total}</div>
                      <div className="col-rar-bar-bg">
                        <div
                          className="col-rar-bar-fill"
                          style={{ width: total > 0 ? `${((owned / total) * 100).toFixed(0)}%` : '0%' }}
                        />
                      </div>
                      <div className="col-rar-pct">{total > 0 ? ((owned / total) * 100).toFixed(0) : 0}%</div>
                    </div>
                  ))}
                </div>
                {/* Foil collection */}
                <div className="collection-foil-section">
                  <div className="collection-foil-title">FOIL COLLECTION</div>
                  {['silver','gold','rainbow'].map(foilType => {
                    const foilConf = FOIL_TYPES[foilType];
                    const count    = (playerData.inventory || []).filter(e => parseInvEntry(e).foil === foilType).length;
                    return (
                      <div key={foilType} className="collection-foil-row">
                        <span className="foil-badge" style={{ color: foilConf.color }}>{foilConf.label}</span>
                        <span className="col-foil-count">{count} owned</span>
                        <span className="col-foil-chance">{foilConf.chance < 1 ? foilConf.chance.toFixed(2) : foilConf.chance}% drop rate</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        )}

        {/* ════ VAULT ════ */}
        {activeTab === 'VAULT' && (
          <div className="tab-fade" style={{ padding: '0 25px' }}>
            <div className="vault-panel">
              <div className="vault-header"><span>🏦</span><h2>VAULT</h2></div>
              <p className="vault-subtitle">No cap. Never lost. Stars, Ash, or Gems.</p>
              <div className="vault-currency-toggle">
                {[
                  { key: 'stars', label: '⭐ STARS' },
                  { key: 'ash',   label: '🔥 ASH'   },
                  { key: 'gems',  label: '💎 GEMS'   },
                ].map(({ key, label }) => (
                  <button
                    key={key}
                    className={`vault-toggle-btn ${vaultCurrency === key ? 'active' : ''}`}
                    onClick={() => { setVaultCurrency(key); setVaultInput(''); }}
                  >
                    {label}
                  </button>
                ))}
              </div>
              <div className="vault-balances">
                <div className="vault-balance-box">
                  <span className="label">WALLET</span>
                  <span className="vault-amount wallet-amount">{vWallet.toLocaleString()}</span>
                </div>
                <div className="vault-divider">⇄</div>
                <div className="vault-balance-box">
                  <span className="label">VAULT</span>
                  <span className="vault-amount vault-amount-gold">{vVault.toLocaleString()}</span>
                </div>
              </div>
              <div className="vault-input-row">
                <input
                  className="vault-input" type="number" min="1"
                  placeholder="ENTER AMOUNT..."
                  value={vaultInput}
                  onChange={e => setVaultInput(e.target.value)}
                />
              </div>
              <div className="vault-quick-btns">
                {[100, 500, 1000, 5000].map(amt => (
                  <button key={amt} className="vault-quick-btn" onClick={() => setVaultInput(String(amt))}>
                    {amt.toLocaleString()}
                  </button>
                ))}
                <button className="vault-quick-btn" onClick={() => setVaultInput(String(vWallet))}>MAX WALLET</button>
                <button className="vault-quick-btn" onClick={() => setVaultInput(String(vVault))}>MAX VAULT</button>
              </div>
              <div className="vault-action-row">
                <button className="vault-btn deposit-btn"  onClick={handleVaultDeposit}>↓ DEPOSIT</button>
                <button className="vault-btn withdraw-btn" onClick={handleVaultWithdraw}>↑ WITHDRAW</button>
              </div>
            </div>
          </div>
        )}

        {/* ════ SHOP ════ */}
        {activeTab === 'SHOP' && (
          <div className="tab-fade shop-container">
            <div className="shop-sub-tabs">
              {[
                { key: 'daily',      label: '📅 DAILY'     },
                { key: 'upgrades',   label: '⬆️ UPGRADES'   },
                { key: 'battlepass', label: '🏆 BATTLE PASS' },
                { key: 'market',     label: '🛒 MARKET'     },
                { key: 'typecraft',  label: '🎟️ PROMO CODES' },
              ].map(({ key, label }) => (
                <button
                  key={key}
                  className={`shop-sub-btn ${shopSubTab === key ? 'active' : ''}`}
                  onClick={() => setShopSubTab(key)}
                >
                  {label}
                </button>
              ))}
            </div>

            {/* Daily */}
            {shopSubTab === 'daily' && (
              <div className="daily-section">
                <div className="daily-header">
                  <span className="daily-title">DAILY REWARD</span>
                  <span className={`daily-status ${dailyCanClaim ? 'available' : 'claimed-status'}`}>
                    {dailyCanClaim ? '● AVAILABLE' : '✓ CLAIMED'}
                  </span>
                </div>
                {streakBroken && (
                  <div className="streak-broken-warning">⚠️ YOU MISSED A DAY — STREAK RESET</div>
                )}
                <div className="daily-reward-display">
                  <div className="daily-reward-content">
                    {todayReward.stars > 0 && <span className="daily-reward-stars">{todayReward.stars.toLocaleString()} ⭐</span>}
                    {todayReward.ash   > 0 && <span className="daily-reward-ash">{todayReward.ash.toLocaleString()} ASH</span>}
                    {todayReward.gems  > 0 && <span className="daily-reward-gems">{todayReward.gems} 💎</span>}
                  </div>
                  <span className="daily-day-label">DAY {Math.min(streakBroken ? 1 : dailyStreak + 1, 100)} / 100</span>
                </div>
                <div className="daily-100-bar-bg">
                  <div className="daily-100-bar-fill" style={{ width: `${Math.min((dailyStreak / 100) * 100, 100).toFixed(1)}%` }} />
                </div>
                <div className="daily-100-label">{dailyStreak < 100 ? `${100 - dailyStreak} DAYS TO COMPLETE` : 'COMPLETE!'}</div>
                <div className="daily-streak-dots">
                  {Array.from({ length: 7 }).map((_, i) => {
                    const dayNum    = Math.floor(dailyStreak / 7) * 7 + i;
                    const isDone    = dayNum < dailyStreak;
                    const isCurrent = dayNum === dailyStreak;
                    return <div key={i} className={`streak-dot ${isDone ? 'dot-done' : ''} ${isCurrent ? 'dot-current' : ''}`} />;
                  })}
                </div>
                <div className="daily-week-label">WEEK {Math.floor(dailyStreak / 7) + 1}</div>
                <button
                  className={`daily-claim-btn ${dailyCanClaim ? 'daily-claim-active' : ''}`}
                  onClick={handleDailyClaim} disabled={!dailyCanClaim}
                >
                  {dailyCanClaim ? 'CLAIM REWARD' : 'COME BACK TOMORROW'}
                </button>
                <div className="daily-upcoming-label">UPCOMING</div>
                <div className="daily-upcoming-list">
                  {DAILY_REWARDS.slice(streakIndex, Math.min(streakIndex + 7, DAILY_REWARDS.length)).map((r, i) => (
                    <div key={i} className={`daily-upcoming-item ${i === 0 && dailyCanClaim ? 'upcoming-today' : ''}`}>
                      <span className="upcoming-day">DAY {streakIndex + i + 1}</span>
                      <span className="upcoming-reward">
                        {r.stars > 0 && <span className="upcoming-stars">{r.stars.toLocaleString()} ⭐</span>}
                        {r.ash   > 0 && <span className="upcoming-ash">{r.ash.toLocaleString()} ASH</span>}
                        {r.gems  > 0 && <span className="upcoming-gems">{r.gems} 💎</span>}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Upgrades */}
            {shopSubTab === 'upgrades' && (
              <div>
                <div className="p-points-banner">
                  LEVEL POINTS: {playerData.lPoints || 0} &nbsp;·&nbsp; EARN 1 PER LEVEL UP
                </div>
                <div className="shop-grid">
                  {[
                    { type: 'cap',     label: 'CAPACITY', desc: `+${(playerData.upgrades?.cap     || 0) * 500} MAX STARS` },
                    { type: 'speed',   label: 'SPEED',    desc: `-${(playerData.upgrades?.speed   || 0) * 10}ms REGEN`   },
                    { type: 'luck',    label: 'LUCK',     desc: `LVL ${playerData.upgrades?.luck  || 0} (rarity only)`   },
                    { type: 'offline', label: 'OFFLINE',  desc: `+${(playerData.upgrades?.offline || 0) * 0.1} ⭐/sec`   },
                  ].map(({ type, label, desc }) => (
                    <div key={type} className="shop-card">
                      <h3>{label}</h3>
                      <div className="shop-value">{desc}</div>
                      <button onClick={() => buyUpgrade(type)}>UPGRADE (1 PT)</button>
                    </div>
                  ))}
                  <div className="shop-card">
                    <h3>AD REWARD</h3>
                    <button className="ad-btn" onClick={() => watchRewardedAd(handleAdReward)}>FREE 1000 ⭐</button>
                  </div>
                </div>
                
                {/* Cost Reduction Upgrades */}
                <div className="gem-exchange-header">💰 COST REDUCTION</div>
                <div className="shop-grid">
                  {COST_REDUCTION_UPGRADES.map(upgrade => {
                    const currentTier = playerData.costReductionTier || 0;
                    const isPurchased = currentTier >= upgrade.tier;
                    const isAvailable = currentTier + 1 === upgrade.tier;
                    const totalDiscount = currentTier * 1; // 1% per tier
                    
                    return (
                      <div key={upgrade.tier} className={`shop-card gem-shop-card ${isPurchased ? 'purchased' : ''} ${!isAvailable && !isPurchased ? 'locked' : ''}`}>
                        <h3>{upgrade.name}</h3>
                        <div className="shop-value">
                          {isPurchased ? '✅ PURCHASED' : isAvailable ? `${upgrade.discount * 100}% OFF` : 'LOCKED'}
                        </div>
                        <div className="shop-desc">Total: {totalDiscount}% discount</div>
                        {isAvailable && (
                          <>
                            <div className="gem-cost">{upgrade.cost.toLocaleString()} 💎</div>
                            <button 
                              onClick={() => buyCostReductionUpgrade(upgrade.tier)}
                              disabled={localGems < upgrade.cost}
                            >
                              BUY ({upgrade.cost.toLocaleString()} 💎)
                            </button>
                          </>
                        )}
                        {isPurchased && (
                          <div className="purchased-badge">OWNED</div>
                        )}
                      </div>
                    );
                  })}
                </div>
                {/* Gem exchange */}
                <div className="gem-exchange-header">💎 GEM EXCHANGE</div>
                <div className="shop-grid">
                  <div className="shop-card gem-shop-card">
                    <h3>STAR BOOST</h3>
                    <div className="shop-value">5,000 ⭐</div>
                    <div className="gem-cost">10 💎</div>
                    <button onClick={async () => {
                      if (localGems < 10) return alert('NEED 10 💎');
                      const discountTier = playerData.costReductionTier || 0;
                      const discountedCost = getDiscountedPrice(10, discountTier);
                      const bonusAmount = 5000; // Base amount
                      
                      const ng = localGems - discountedCost;
                      const ns = Math.min(localStars + bonusAmount, FIELD_CAPS.stars);
                      setLocalGems(ng); setLocalStars(ns);
                      await updateDoc(doc(db, 'users', user.uid), { gems: ng, stars: ns });
                      triggerSavePulse();
                    }}>BUY ({getDiscountedPrice(10, playerData.costReductionTier || 0)} 💎)</button>
                  </div>
                  <div className="shop-card gem-shop-card">
                    <h3>ASH BOOST</h3>
                    <div className="shop-value">500 ASH</div>
                    <div className="gem-cost">10 💎</div>
                    <button onClick={async () => {
                      if (localGems < 10) return alert('NEED 10 💎');
                      const discountTier = playerData.costReductionTier || 0;
                      const discountedCost = getDiscountedPrice(10, discountTier);
                      const bonusAmount = 500; // Base amount
                      
                      const ng = localGems - discountedCost;
                      const na = Math.min(localAsh + bonusAmount, FIELD_CAPS.ash);
                      setLocalGems(ng); setLocalAsh(na);
                      await updateDoc(doc(db, 'users', user.uid), { gems: ng, ash: na });
                      triggerSavePulse();
                    }}>BUY ({getDiscountedPrice(10, playerData.costReductionTier || 0)} 💎)</button>
                  </div>
                </div>
              </div>
            )}

            {/* Battle pass */}
            {shopSubTab === 'battlepass' && (
              <div className="bp-section">
                <div className="bp-header">
                  <span className="bp-title">BATTLE PASS</span>
                  <span className="bp-month">
                    {new Date().toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                  </span>
                </div>
                <div className="bp-progress-row">
                  <span className="bp-progress-label">MONTHLY POWER GAINED</span>
                  <span className="bp-progress-val">{monthlyGain.toLocaleString()} PWR</span>
                </div>
                <div className="bp-progress-bar-bg">
                  <div className="bp-progress-bar-fill"
                    style={{ width: `${Math.min((monthlyGain / BP_TIERS[BP_TIERS.length - 1].powerReq) * 100, 100).toFixed(1)}%` }}
                  />
                </div>
                <div className="bp-completion-note">
                  {unlockedTierCount}/{BP_TIERS.length} TIERS · RESETS MONTHLY
                </div>
                <div className="bp-tier-scroll">
                  <div className="bp-tier-grid">
                    {BP_TIERS.map((tier, idx) => {
                      const isUnlocked = monthlyGain >= tier.powerReq;
                      const isClaimed  = bpClaimed.includes(idx);
                      return (
                        <div key={idx} className={`bp-tier ${isUnlocked ? 'bp-unlocked' : ''} ${isClaimed ? 'bp-claimed' : ''}`}>
                          <div className="bp-tier-num">T{idx + 1}</div>
                          <div className="bp-tier-req">
                            {tier.powerReq >= 1000000
                              ? `${(tier.powerReq/1000000).toFixed(1)}M`
                              : tier.powerReq >= 1000
                                ? `${(tier.powerReq/1000).toFixed(0)}K`
                                : tier.powerReq}
                          </div>
                          <div className="bp-tier-reward">
                            {tier.stars > 0 && <span className="bp-stars">{tier.stars >= 1000 ? `${(tier.stars/1000).toFixed(0)}K` : tier.stars}⭐</span>}
                            {tier.ash   > 0 && <span className="bp-ash">{tier.ash}A</span>}
                            {tier.gems  > 0 && <span className="bp-gems">{tier.gems}💎</span>}
                            {tier.cardReward && <span className="bp-card-reward">🃏</span>}
                          </div>
                          {isUnlocked && !isClaimed && (
                            <button className="bp-claim-btn" onClick={() => handleBPClaim(idx)}>CLAIM</button>
                          )}
                          {isClaimed && <div className="bp-claimed-tick">✓</div>}
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}

            {/* Market */}
            {shopSubTab === 'market' && (
              <div className="market-container">
                <div className="search-bar-wrap">
                  <input
                    className="search-input"
                    type="text"
                    placeholder="SEARCH MARKET..."
                    value={marketSearch}
                    onChange={e => setMarketSearch(e.target.value)}
                  />
                  {marketSearch && (
                    <button className="search-clear" onClick={() => setMarketSearch('')}>✕</button>
                  )}
                </div>
                <div className="market-grid">
                  {filteredMarket.length > 0 ? filteredMarket.map(item => {
                    const entry   = item.cardEntry || item.cardName;
                    const { foil } = parseInvEntry(entry);
                    const foilConf = foil ? FOIL_TYPES[foil] : null;
                    return (
                      <div key={item.id} className="market-item">
                        <div className="m-info">
                          <b>{item.cardName}{foilConf ? <span style={{ color: foilConf.color }}> [{foilConf.label}]</span> : ''}</b>
                          <span className="m-price">{item.price.toLocaleString()} 💎</span>
                        </div>
                        <p className="m-seller">SOLD BY: {item.sellerName}</p>
                        <button className="btn-buy" onClick={() => buyFromMarket(item)}>BUY ({item.price} 💎)</button>
                      </div>
                    );
                  }) : (
                    <p className="empty-msg">
                      {marketSearch ? 'NO RESULTS' : 'NO CARDS LISTED ON MARKET'}
                    </p>
                  )}
                </div>
              </div>
            )}

            {/* Typecraft */}
            {shopSubTab === 'typecraft' && (
              <div className="typecraft-section">
                <div className="typecraft-header">
                  <span className="typecraft-icon">⌨</span>
                  <div>
                    <div className="typecraft-title">TYPECRAFT REWARDS</div>
                    <div className="typecraft-sub">Redeem codes from Typecraft typing game</div>
                  </div>
                </div>
                <div className="typecraft-info">
                  <p>🎁 Every 10 levels in Typecraft = 10 Gems in FallingStar TCG!</p>
                  <p style={{fontSize: '0.8rem', color: '#888', marginTop: '8px'}}>
                    Use the same email on both sites for automatic redemption
                  </p>
                </div>
                <div className="typecraft-input-area">
                  <input
                    type="text"
                    id="typecraft-code-input"
                    className="typecraft-code-input"
                    placeholder="ENTER CODE (e.g., TCABC1234)"
                    maxLength="10"
                  />
                  <button
                    className="typecraft-redeem-btn"
                    onClick={async () => {
                      const codeInput = document.getElementById('typecraft-code-input');
                      const code = codeInput.value.trim().toUpperCase();
                      if (!code) return alert('Please enter a code');
                      if (!code.startsWith('TC')) return alert('Invalid code format');

                      try {
                        const codeDoc = await getDoc(doc(db, 'typecraft_codes', code));
                        if (!codeDoc.exists()) return alert('Invalid code');

                        const codeData = codeDoc.data();
                        if (codeData.redeemed) return alert('This code has already been redeemed');

                        const newGems = Math.min(localGems + 10, FIELD_CAPS.gems);
                        setLocalGems(newGems);

                        await updateDoc(doc(db, 'typecraft_codes', code), {
                          status: 'redeemed',
                          redeemed: true,
                          redeemedAt: new Date(),
                          redeemedBy: user.uid
                        });

                        await updateDoc(doc(db, 'users', user.uid), { gems: newGems });

                        codeInput.value = '';
                        triggerSavePulse();
                        alert('✅ 10 Gems added!');
                      } catch (error) {
                        console.error('Redeem error:', error);
                        alert('Error redeeming code');
                      }
                    }}
                  >
                    REDEEM (10 💎)
                  </button>
                </div>
                <div className="typecraft-howto">
                  <h4>How to get codes:</h4>
                  <ol>
                    <li>Play Typecraft at <a href="https://infinitypixel-lab.github.io/Typecraft/" target="_blank" rel="noopener noreferrer">infinitypixel-lab.github.io/Typecraft</a></li>
                    <li>Reach every 10th level (10, 20, 30...)</li>
                    <li>Get your reward code automatically!</li>
                    <li>Or use same email login - gems are added automatically!</li>
                  </ol>
                </div>
              </div>
            )}
          </div>
        )}

      

      {/* ════ RANKING ════ */}
      {activeTab === 'RANKING' && (
        <div className="tab-fade ranking-container">
          <div className="ranking-header">
            <h2>🏆 TOP PLAYERS</h2>
            <p>Ranked by Level</p>
          </div>
          <div className="ranking-list">
            {leaderboard.length === 0 ? (
              <div className="ranking-empty">Loading...</div>
            ) : (
              leaderboard.map((player, idx) => (
                <div key={player.id} className={`ranking-item ${idx < 3 ? 'top-' + (idx + 1) : ''}`}>
                  <div className="ranking-rank">
                    {idx === 0 ? '🥇' : idx === 1 ? '🥈' : idx === 2 ? '🥉' : `#${idx + 1}`}
                  </div>
                  <div className="ranking-info">
                    <div className="ranking-name">{player.bio || 'Explorer'}</div>
                    <div className="ranking-stats">Level {player.level || 1} • {(player.totalPower || 0).toLocaleString()} PWR</div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}
      </main>

      {/* ── FUSION CASE MODAL ── */}
      {fusionTarget && (
        <FusionCase
          cardName={fusionTarget.cardName}
          cardCount={(playerData.inventory || []).filter(e => {
            const p = parseInvEntry(e);
            return p.name === fusionTarget.cardName && !p.foil;
          }).length}
          onClose={() => setFusionTarget(null)}
          onDeduct={() => handleFusionDeduct(fusionTarget.cardName)}
          onCollect={handleFusionCollect}
        />
      )}

      {/* ── BP CARD REWARD MODAL ── */}
      {bpCardModal && (() => {
        const card = CARD_LIBRARY.find(c => c.name === bpCardModal);
        return (
          <div className="modal-overlay" onClick={() => setBpCardModal(null)}>
            <div className="bp-card-modal tab-fade" onClick={e => e.stopPropagation()}>
              <h2>BATTLE PASS REWARD!</h2>
              <div className={`game-card ${card?.rarity?.toLowerCase() || 'divine'}`} style={{ maxWidth: '200px', alignSelf: 'center', margin: '16px auto' }}>
                <span className="rarity-label">{card?.rarity}</span>
                <h3 className="card-title">{bpCardModal}</h3>
                <p className="card-pwr">+{card?.power.toLocaleString()} PWR</p>
              </div>
              <button className="collect-btn" onClick={() => setBpCardModal(null)}>COLLECT</button>
            </div>
          </div>
        );
      })()}

      {/* ── ASH MODAL ── */}
      {showAshModal && (() => {
        const { invEntry, cardName } = showAshModal;
        const { foil }   = parseInvEntry(invEntry);
        const card        = CARD_LIBRARY.find(c => c.name === cardName);
        const foilConf    = foil ? FOIL_TYPES[foil] : null;
        const ashVal      = Math.floor((ASH_VALUES[card?.rarity] || 2) * (foilConf?.multiplier || 1));
        return (
          <div className="modal-overlay">
            <div className="ash-modal tab-fade">
              <h2>ASH THIS CARD?</h2>
              <div className={`game-card ${card?.rarity?.toLowerCase() || 'common'} ${foil ? 'foil-' + foil : ''}`}
                style={{ marginBottom: '16px', maxWidth: '200px', alignSelf: 'center' }}>
                <span className="rarity-label">{card?.rarity}</span>
                {foilConf && <span className="foil-badge" style={{ color: foilConf.color }}>{foilConf.label}</span>}
                <h3 className="card-title">{cardName}</h3>
                <p className="card-pwr">+{getEntryPower(invEntry).toLocaleString()} PWR</p>
              </div>
              <p className="ash-modal-reward">+{ashVal} ASH</p>
              <p className="ash-modal-warning">THIS CANNOT BE UNDONE</p>
              <div className="ash-modal-actions">
                <button className="ash-confirm-btn" onClick={handleAshCard}>CONFIRM</button>
                <button className="close-btn" onClick={() => setShowAshModal(null)}>CANCEL</button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* ── ADMIN PANEL ── */}
      {isAdminOpen && (
        <div className="modal-overlay">
          <div className="admin-panel tab-fade">
            <h2>ADMIN PANEL</h2>
            <div style={{ background: '#0a0a0a', padding: '15px', borderRadius: '8px', border: '1px solid #00e1ff', marginBottom: '20px', textAlign: 'left' }}>
              <h3 style={{ color: '#00e1ff', fontSize: '0.8rem', margin: '0 0 10px 0', letterSpacing: '1px' }}>PROJECT ECONOMICS</h3>
              {[
                { label: 'EST. AD REVENUE', value: `+$${(stats.estRevenue || 0).toFixed(2)}`,              color: '#00ff88' },
                { label: 'FIREBASE COST',   value: `-$${Number(stats.estFirebaseCost || 0).toFixed(4)}`, color: '#ff4444' },
              ].map(({ label, value, color }) => (
                <div key={label} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.85rem', marginBottom: '5px' }}>
                  <span>{label}:</span><span style={{ color }}>{value}</span>
                </div>
              ))}
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.65rem', marginTop: '10px', color: '#888' }}>
                <span>USERS: {stats.totalUsers || 0}</span><span>ADS: {stats.adsWatched || 0}</span>
              </div>
              <button onClick={updateProjectStats} style={{ marginTop: '10px', width: '100%', padding: '5px', fontSize: '0.7rem', background: '#222', color: '#fff', border: '1px solid #444', cursor: 'pointer' }}>
                REFRESH DATA
              </button>
            </div>
            {[
              { label: 'EVENT MODE',  action: () => updateDoc(doc(db, 'global_events', 'current'), { active: !eventActive }),        value: eventActive   ? 'DISABLE' : 'ENABLE' },
              { label: 'MAINTENANCE', action: () => updateDoc(doc(db, 'global_events', 'current'), { maintenance: !isMaintenance }), value: isMaintenance ? 'DISABLE' : 'ENABLE' },
            ].map(({ label, action, value }) => (
              <div key={label} className="admin-row"><span>{label}</span><button onClick={action}>{value}</button></div>
            ))}
            <div className="admin-row"><span>STARS +5000</span>
              <button onClick={() => { const n = Math.min(localStars + 5000, FIELD_CAPS.stars); setLocalStars(n); updateDoc(doc(db, 'users', user.uid), { stars: n }); }}>ADD</button>
            </div>
            <div className="admin-row"><span>ASH +500</span>
              <button onClick={() => { const n = Math.min(localAsh + 500, FIELD_CAPS.ash); setLocalAsh(n); updateDoc(doc(db, 'users', user.uid), { ash: n }); }}>ADD</button>
            </div>
            <div className="admin-row"><span>GEMS +50</span>
              <button onClick={() => { const n = Math.min(localGems + 50, FIELD_CAPS.gems); setLocalGems(n); updateDoc(doc(db, 'users', user.uid), { gems: n }); }}>ADD</button>
            </div>
            <div style={{ background: '#0a0a0a', padding: '15px', borderRadius: '8px', border: '1px solid #ffd700', marginBottom: '20px', textAlign: 'left' }}>
              <h3 style={{ color: '#ffd700', fontSize: '0.8rem', margin: '0 0 10px 0', letterSpacing: '1px' }}>CREATE PROMO CODE</h3>
              <div style={{ display: 'flex', gap: '8px', marginBottom: '10px' }}>
                <input
                  type="number"
                  id="admin-promo-gems"
                  placeholder="Gems amount"
                  min="1"
                  max="1000"
                  style={{ flex: 1, padding: '8px', background: '#111', border: '1px solid #333', color: '#fff', borderRadius: '4px' }}
                />
                <button
                  onClick={async () => {
                    const gemsInput = document.getElementById('admin-promo-gems');
                    const gems = parseInt(gemsInput.value);
                    if (!gems || gems < 1) return alert('Please enter valid gem amount');
                    try {
                      const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
                      let code = 'FS';
                      for (let i = 0; i < 8; i++) code += chars[Math.floor(Math.random() * chars.length)];
                      await setDoc(doc(db, 'promo_codes', code), {
                        code, gems, createdAt: new Date(), createdBy: user.uid,
                        redeemed: false, redeemedBy: null, redeemedAt: null
                      });
                      gemsInput.value = '';
                      alert(`✅ Promo code created: ${code} (${gems} gems)`);
                    } catch (error) {
                      console.error('Create promo code error:', error);
                      alert('Error creating promo code');
                    }
                  }}
                  style={{ padding: '8px 16px', background: '#ffd700', color: '#000', border: 'none', borderRadius: '4px', cursor: 'pointer', fontWeight: 'bold' }}
                >
                  CREATE
                </button>
              </div>
            </div>
            <div className="admin-broadcast-zone">
              <input type="text" placeholder="BROADCAST MESSAGE..." value={broadcastMsg} onChange={e => setBroadcastMsg(e.target.value)} />
              <button onClick={() => { setDoc(doc(db, 'global_events', 'broadcast'), { text: broadcastMsg }); setBroadcastMsg(''); }}>SEND</button>
            </div>
            <button className="close-btn" onClick={() => setIsAdminOpen(false)}>CLOSE</button>
          </div>
        </div>
      )}

      {/* ── OFFLINE REWARD ── */}
      {offlineData && (
        <div className="modal-overlay">
          <div className="welcome-modal tab-fade">
            <h2>WELCOME BACK!</h2>
            <p>YOU WERE AWAY FOR {Math.floor(offlineData.time / 60)} MINUTES.</p>
            <div className="offline-stars">⭐ +{offlineData.stars}</div>
            <button className="collect-btn" onClick={() => {
              const n = Math.min(localStars + offlineData.stars, maxStars);
              setLocalStars(n); setOfflineData(null);
              if (user) updateDoc(doc(db, 'users', user.uid), { stars: n, lastOnline: Date.now() });
            }}>COLLECT</button>
          </div>
        </div>
      )}

      {/* ── PACK REVEAL ── */}
      {(isOpening || pulledCards.length > 0) && (
        <div className="modal-overlay" onClick={() => !isOpening && setPulledCards([])}>
          <div className={`pack-reveal-zone ${isOpening ? 'is-shaking' : ''} ${hasDivine ? 'divine-shake' : ''}`}>
            {isOpening ? (
              <div className="sealed-pack-visual">
                <div className="pack-glow"></div>
                <div className="pack-text">OPENING...</div>
              </div>
            ) : (
              <>
                <div className="reveal-tap-hint">TAP ANYWHERE TO CLOSE</div>
                <div className="card-reveal-scroll" onClick={e => e.stopPropagation()}>
                  <div className="card-reveal-stagger">
                    {pulledCards.map((c, i) => {
                      const foilConf = c.foil ? FOIL_TYPES[c.foil] : null;
                      return (
                        <div key={i} className={`reveal-item vfx-${c.rarity?.toLowerCase()} ${c.foil ? 'reveal-foil-' + c.foil : ''}`}>
                          {foilConf && (
                            <div className="reveal-foil-badge" style={{ color: foilConf.color }}>
                              ✦ {foilConf.label} FOIL ×{foilConf.multiplier}
                            </div>
                          )}
                          <div className="reveal-rarity">{c.rarity}</div>
                          <div className="reveal-name">{c.name}</div>
                          <div className="reveal-power">PWR {getEntryPower(c.invEntry || c.name).toLocaleString()}</div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* ── ACCOUNT MODAL — link OR sign in ── */}
      {showLinkModal && (
        <div className="modal-overlay" onClick={() => !linkSuccess && setShowLinkModal(false)}>
          <div className="link-modal tab-fade" onClick={e => e.stopPropagation()}>
            {linkSuccess ? (
              <>
                <div className="link-success-icon">✓</div>
                <h2>{linkMode === 'link' ? 'ACCOUNT SAVED!' : 'WELCOME BACK!'}</h2>
                <p className="link-sub">
                  {linkMode === 'link'
                    ? <>Your progress is now tied to<br /><strong>{linkEmail}</strong></>
                    : <>Signed in as<br /><strong>{linkEmail}</strong></>}
                </p>
                {linkMode === 'link' && (
                  <p className="link-sub" style={{ color: '#555' }}>Sign in with this email on any device to recover your account.</p>
                )}
                <button className="link-submit-btn" onClick={() => setShowLinkModal(false)}>DONE</button>
              </>
            ) : (
              <>
                <h2>{linkMode === 'link' ? 'SAVE YOUR ACCOUNT' : 'SIGN IN'}</h2>
                <p className="link-sub">
                  {linkMode === 'link'
                    ? 'Link an email and password so you never lose your progress.'
                    : 'Sign in with your email to recover your account.'}
                </p>
                {linkError && <div className="link-error">{linkError}</div>}
                <input
                  className="link-input"
                  type="email"
                  placeholder="EMAIL ADDRESS"
                  value={linkEmail}
                  onChange={e => setLinkEmail(e.target.value)}
                  autoComplete="email"
                />
                <input
                  className="link-input"
                  type="password"
                  placeholder={linkMode === 'link' ? 'PASSWORD (min 6 chars)' : 'PASSWORD'}
                  value={linkPassword}
                  onChange={e => setLinkPassword(e.target.value)}
                  autoComplete={linkMode === 'link' ? 'new-password' : 'current-password'}
                />
                <button
                  className="link-submit-btn"
                  onClick={async () => {
                    setLinkError('');
                    if (!linkEmail || !linkPassword) return setLinkError('PLEASE FILL IN BOTH FIELDS.');
                    try {
                      if (linkMode === 'link') {
                        if (linkPassword.length < 6) return setLinkError('PASSWORD MUST BE AT LEAST 6 CHARACTERS.');
                        const credential = EmailAuthProvider.credential(linkEmail, linkPassword);
                        await linkWithCredential(user, credential);
                      } else {
                        await signInWithEmailAndPassword(auth, linkEmail, linkPassword);
                      }
                      setLinkSuccess(true);
                    } catch (e) {
                      if (e.code === 'auth/email-already-in-use')   setLinkError('THAT EMAIL IS ALREADY LINKED TO ANOTHER ACCOUNT.');
                      else if (e.code === 'auth/invalid-email')      setLinkError('PLEASE ENTER A VALID EMAIL ADDRESS.');
                      else if (e.code === 'auth/weak-password')      setLinkError('PASSWORD IS TOO WEAK.');
                      else if (e.code === 'auth/wrong-password')     setLinkError('INCORRECT PASSWORD.');
                      else if (e.code === 'auth/user-not-found')     setLinkError('NO ACCOUNT FOUND WITH THAT EMAIL.');
                      else if (e.code === 'auth/invalid-credential') setLinkError('INCORRECT EMAIL OR PASSWORD.');
                      else setLinkError('SOMETHING WENT WRONG. PLEASE TRY AGAIN.');
                    }
                  }}
                >
                  {linkMode === 'link' ? 'LINK ACCOUNT' : 'SIGN IN'}
                </button>
                {/* Toggle between link and sign-in */}
                <button
                  className="link-cancel-btn"
                  onClick={() => { setLinkMode(m => m === 'link' ? 'signin' : 'link'); setLinkError(''); }}
                >
                  {linkMode === 'link' ? 'ALREADY HAVE AN ACCOUNT? SIGN IN' : 'LINK A NEW ACCOUNT INSTEAD'}
                </button>
                <button className="link-cancel-btn" onClick={() => setShowLinkModal(false)}>CANCEL</button>
              </>
            )}
          </div>
        </div>
      )}

      <BottomBanner adClient="ca-pub-4127085758385970" adSlot="4588032796" />
    </div>
  );
}

export default App;